import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const MAX_SHOWN = 100;
const staffLabel = (s) => `${s.staff_name || s.name || s.staff_id} (${s.staff_id})`;

// Picker dropdown with a search box: matches any part of the staff name or staff ID.
export const StaffPickerSelect = ({ staff, value, onChange, placeholder = 'Select picker...', className, testId = 'staff-picker' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = staff.find((s) => s.staff_id === value);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t) return staff.filter((s) => staffLabel(s).toLowerCase().includes(t)).slice(0, MAX_SHOWN);
    // Unfiltered: keep the current pick pinned on top so its check mark is always visible.
    const rest = staff.filter((s) => s.staff_id !== value);
    return (selected ? [selected, ...rest] : rest).slice(0, MAX_SHOWN);
  }, [staff, q, value, selected]);

  const pick = (id) => { onChange(id); setOpen(false); setQ(''); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className={cn('justify-between font-normal', !selected && 'text-muted-foreground', className)}
          data-testid={`${testId}-select`}>
          <span className="truncate">{selected ? staffLabel(selected) : placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <div className="flex items-center gap-2 px-3 border-b">
          <Search className="w-4 h-4 opacity-50 shrink-0" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && matches.length === 1) pick(matches[0].staff_id); }}
            placeholder="Search name or staff ID..."
            className="flex-1 h-10 bg-transparent text-sm outline-none" data-testid={`${testId}-search`} />
        </div>
        <div className="max-h-72 overflow-y-auto p-1" data-testid={`${testId}-options`}>
          {matches.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500" data-testid={`${testId}-empty`}>No staff found</p>
          ) : matches.map((s) => (
            <button key={s.staff_id} type="button" onClick={() => pick(s.staff_id)}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left hover:bg-accent"
              data-testid={`${testId}-option-${s.staff_id}`}>
              <Check className={cn('w-4 h-4 shrink-0', s.staff_id === value ? 'opacity-100' : 'opacity-0')} />
              <span className="truncate">{staffLabel(s)}</span>
            </button>
          ))}
          {matches.length === MAX_SHOWN && (
            <p className="px-2 py-1 text-xs text-gray-400">Showing first {MAX_SHOWN}. Type to narrow down.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
